import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useEmailStore } from '../../stores/email-store';
import type { CachedEmail } from '../../stores/email-store';
import { useAppStore } from '../../stores/appStore';
import { useAuthStore } from '../../stores/authStore';
import { useToastStore } from '../../stores/toast-store';
import { API_BASE } from '../../lib/api-config';
import { PageHeader } from '../shared-ui/PageHeader';
import { PrimaryButton } from '../shared-ui/PrimaryButton';
import { PersonAvatar } from '../today/PersonAvatar';
import { decodeHtmlEntities } from '../../lib/html-utils';

// Icons (Material Design paths, matching Gmail's style)

function IconArchive() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
      <path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z" />
    </svg>
  );
}

function IconDelete() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );
}

function IconMarkRead() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
  );
}

function IconMarkUnread() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z" />
    </svg>
  );
}

function IconCompose() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M4 20h4.25L19.67 8.58l-4.25-4.25L4 15.75V20zm2-3.42 9.42-9.42 1.42 1.42L7.42 18H6v-1.42zM17.13 2.62a1.5 1.5 0 0 1 2.12 0l2.13 2.13a1.5 1.5 0 0 1 0 2.12l-1 1-4.25-4.25 1-1z" />
    </svg>
  );
}

/**
 * Inbox - handoff-style card list. Each row is an EmailCard with
 * priority ribbon on the left edge, sender + subject + preview in the
 * middle, time + unread dot on the right. Clicking opens the Focus pane.
 */

export function InboxView() {
  return <MailListView mode="inbox" />;
}

export function ArchiveView() {
  return <MailListView mode="archive" />;
}

function MailListView({ mode }: { mode: 'inbox' | 'archive' }) {
  const emails = useEmailStore((s) => s.emails);
  const activeProvider = useEmailStore((s) => s.activeProvider);
  const isLoading = useEmailStore((s) => s.isLoading);
  const error = useEmailStore((s) => s.error);
  const loadEmails = useEmailStore((s) => s.loadEmails);
  const token = useAuthStore((s) => s.token);
  const setActiveEmailId = useAppStore((s) => s.setActiveEmailId);
  const archived = mode === 'archive';

  const openCompose = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('signa:compose', {
        detail: { to: '', subject: '', body: '' },
      })
    );
  }, []);

  useEffect(() => {
    if (!activeProvider || token === 'demo-token') return;
    void loadEmails(activeProvider, undefined, archived);
  }, [activeProvider, archived, loadEmails, token]);

  useEffect(() => {
    if (archived) return;

    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const isEditable =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;

      if (isEditable || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() !== 'c') return;

      event.preventDefault();
      openCompose();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [archived, openCompose]);

  const sorted = useMemo(() => {
    const seen = new Set<string>();
    const deduped = emails.filter((e) => {
      if (Boolean(e.isArchived) !== archived) return false;
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });
    return deduped.sort((a, b) => {
      const ua = !a.isRead ? 1 : 0;
      const ub = !b.isRead ? 1 : 0;
      if (ua !== ub) return ub - ua;
      return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
    });
  }, [emails, archived]);

  const unread = useMemo(
    () => sorted.filter((e) => !e.isRead).length,
    [sorted]
  );

  const subtitle = archived
    ? `${sorted.length} archived message${sorted.length === 1 ? '' : 's'}`
    : unread > 0
    ? `${unread} unread`
    : `${sorted.length} message${sorted.length === 1 ? '' : 's'} - all caught up`;
  const noProvider = !activeProvider && emails.length === 0;

  return (
    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <div
        data-testid="mail-list-content"
        style={{
          maxWidth: 980,
          margin: 0,
          padding: '40px 44px 120px',
          width: '100%',
        }}
      >
        <PageHeader
          title={archived ? 'Archive' : 'Inbox'}
          subtitle={subtitle}
          right={
            archived ? undefined : (
              <PrimaryButton
                icon={<IconCompose />}
                shortcut="C"
                onClick={openCompose}
              >
                Compose
              </PrimaryButton>
            )
          }
        />

        {noProvider ? (
          <EmptyState archived={archived} noProvider />
        ) : error ? (
          <ErrorState
            message={error}
            onRetry={() => {
              const provider = activeProvider;
              if (provider) void loadEmails(provider, undefined, archived);
            }}
          />
        ) : isLoading && sorted.length === 0 ? (
          <LoadingState label={archived ? 'Loading archive...' : 'Loading inbox...'} />
        ) : sorted.length === 0 ? (
          <EmptyState archived={archived} />
        ) : (
          <div>
            {sorted.map((e) => (
              <EmailCard
                key={e.id}
                email={e}
                showArchiveAction={!archived}
                onOpen={() => setActiveEmailId(e.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Card

function EmailCard({
  email,
  onOpen,
  showArchiveAction = true,
}: {
  email: CachedEmail;
  onOpen: () => void;
  showArchiveAction?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const [unsubState, setUnsubState] = useState<'idle' | 'loading' | 'done'>('idle');
  const archiveEmail = useEmailStore((s) => s.archiveEmail);
  const trashEmail = useEmailStore((s) => s.trashEmail);
  const markRead = useEmailStore((s) => s.markRead);
  const token = useAuthStore((s) => s.token);
  const addToast = useToastStore((s) => s.addToast);
  const high = isUrgent(email);
  const unread = !email.isRead;

  function stop(e: React.MouseEvent) {
    e.stopPropagation();
  }

  const handleUnsubscribe = useCallback(async (e: React.MouseEvent) => {
    stop(e);
    if (unsubState !== 'idle') return;
    setUnsubState('loading');
    try {
      const res = await fetch(`${API_BASE}/emails/${email.id}/unsubscribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json() as { type: string; url?: string };
      if (data.type === 'url' && data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      } else {
        addToast({ message: 'Unsubscribed successfully', type: 'success' });
      }
      setUnsubState('done');
    } catch {
      addToast({ message: 'Could not unsubscribe. Try again.', type: 'error' });
      setUnsubState('idle');
    }
  }, [email.id, token, addToast, unsubState]);

  const handleArchive = useCallback(async (e: React.MouseEvent) => {
    stop(e);
    try {
      await archiveEmail(email.id);
    } catch {
      addToast({ message: 'Could not archive message. Try again.', type: 'error' });
    }
  }, [archiveEmail, email.id, addToast]);

  const handleDelete = useCallback(async (e: React.MouseEvent) => {
    stop(e);
    try {
      await trashEmail(email.id);
    } catch {
      addToast({ message: 'Could not delete message. Try again.', type: 'error' });
    }
  }, [trashEmail, email.id, addToast]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen()}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 14,
        padding: '16px 20px',
        background: 'var(--card)',
        border: `1px solid ${hover ? 'var(--accent)' : 'var(--rule)'}`,
        borderRadius: 12,
        marginBottom: 10,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'var(--font-ui)',
        transition: 'border-color 150ms ease, transform 150ms ease',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {high && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 2,
            background: 'var(--accent)',
          }}
        />
      )}

      <PersonAvatar
        name={email.fromName || email.fromEmail}
        email={email.fromEmail}
        size={32}
      />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            overflow: 'hidden',
          }}
        >
          <span
            style={{
              fontSize: 14.5,
              fontWeight: unread ? 600 : 500,
              color: 'var(--ink)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 0,
              maxWidth: '40%',
            }}
          >
            {email.fromName || email.fromEmail}
          </span>
          <span
            style={{
              fontSize: 14,
              color: 'var(--ink)',
              fontWeight: unread ? 500 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {decodeHtmlEntities(email.subject)}
          </span>
        </div>
        <div
          style={{
            fontSize: 13.5,
            color: 'var(--ink-soft)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.45,
          }}
        >
          {decodeHtmlEntities(email.snippet)}
        </div>
      </div>

      {/* Right side: time+dot normally, action buttons on hover */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          flexShrink: 0,
          minWidth: hover ? 'auto' : 52,
          justifyContent: 'flex-end',
        }}
      >
        {hover ? (
          <>
            {email.listUnsubscribe && unsubState !== 'done' && (
              <button
                type="button"
                onClick={handleUnsubscribe}
                disabled={unsubState === 'loading'}
                title="Unsubscribe from this mailing list"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '3px 8px',
                  marginRight: 4,
                  background: 'transparent',
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  color: 'var(--ink-soft)',
                  fontSize: 12,
                  fontFamily: 'var(--font-ui)',
                  cursor: unsubState === 'loading' ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                  opacity: unsubState === 'loading' ? 0.6 : 1,
                  transition: 'border-color 120ms, color 120ms',
                }}
                onMouseEnter={(e) => {
                  if (unsubState === 'idle') {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--error)';
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--error)';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--rule)';
                  (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-soft)';
                }}
              >
                {unsubState === 'loading' ? 'Unsubscribing...' : 'Unsubscribe'}
              </button>
            )}
            {showArchiveAction && (
              <HoverAction label="Archive" onClick={handleArchive}>
                <IconArchive />
              </HoverAction>
            )}
            <HoverAction label="Delete" onClick={handleDelete}>
              <IconDelete />
            </HoverAction>
            <HoverAction
              label={unread ? 'Mark as read' : 'Mark as unread'}
              onClick={(e) => { stop(e); markRead(email.id, unread); }}
            >
              {unread ? <IconMarkRead /> : <IconMarkUnread />}
            </HoverAction>
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 6,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-faint)',
                letterSpacing: '0.5px',
              }}
            >
              {formatTime(email.receivedAt)}
            </span>
            {unread && (
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--accent)',
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function HoverAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: 6,
        border: 'none',
        background: 'transparent',
        color: 'var(--ink-soft)',
        cursor: 'pointer',
        transition: 'background 120ms, color 120ms',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'var(--rule)';
        (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-soft)';
      }}
    >
      {children}
    </button>
  );
}

// Helpers

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    now.getFullYear() === d.getFullYear() &&
    now.getMonth() === d.getMonth() &&
    now.getDate() === d.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const diffDays = Math.floor(
    (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function isUrgent(email: CachedEmail): boolean {
  if (email.isStarred) return true;
  const text = `${email.subject} ${email.snippet}`.toLowerCase();
  return /\b(urgent|asap|eod|eow|tomorrow|today|by\s+end|deadline)\b/.test(text);
}

function LoadingState({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: '60px 24px',
        textAlign: 'center',
        color: 'var(--ink-soft)',
        fontSize: 13,
        border: '1px dashed var(--rule)',
        borderRadius: 12,
      }}
    >
      {label}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      style={{
        padding: '28px 24px',
        textAlign: 'center',
        color: 'var(--error)',
        fontSize: 13,
        border: '1px solid var(--error)',
        borderRadius: 12,
        background: 'color-mix(in srgb, var(--error) 7%, transparent)',
      }}
    >
      <div style={{ marginBottom: 12 }}>{message}</div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          padding: '7px 12px',
          borderRadius: 7,
          border: '1px solid var(--error)',
          background: 'transparent',
          color: 'var(--error)',
          cursor: 'pointer',
          fontFamily: 'var(--font-ui)',
          fontSize: 12.5,
        }}
      >
        Retry
      </button>
    </div>
  );
}

function EmptyState({ archived, noProvider = false }: { archived: boolean; noProvider?: boolean }) {
  return (
    <div
      style={{
        padding: '60px 24px',
        textAlign: 'center',
        color: 'var(--ink-faint)',
        fontSize: 13,
        border: '1px dashed var(--rule)',
        borderRadius: 12,
      }}
    >
      {noProvider
        ? 'Connect Gmail to start seeing messages here.'
        : archived
        ? 'Archive is empty. Archived emails will appear here.'
        : 'Inbox is empty. Sync Gmail or wait for new messages.'}
    </div>
  );
}
